import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Input, Spinner, Chip } from '@heroui/react';
import { MagnifyingGlass, User, X } from '@phosphor-icons/react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';

export interface CcBccContact {
  name: string;
  email: string;
}

interface CcBccSearchProps {
  label: string;
  contacts: CcBccContact[];
  onChange: (contacts: CcBccContact[]) => void;
}

export const CcBccSearch: React.FC<CcBccSearchProps> = ({ label, contacts, onChange }) => {
  const { session } = useSession();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchContacts = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/contact/search?query=${encodeURIComponent(query)}`,
        { method: 'GET' },
        session
      );
      if (response.ok) {
        const data = await response.json();
        // Filter out already-selected emails
        const selectedEmails = new Set(contacts.map(c => c.email.toLowerCase()));
        setSearchResults(data.filter((c: any) => c.email && !selectedEmails.has(c.email.toLowerCase())));
      }
    } catch (error) {
      console.error('Error searching contacts for CC/BCC:', error);
    } finally {
      setIsSearching(false);
    }
  }, [session, contacts]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (searchTerm.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => searchContacts(searchTerm), 300);
    } else {
      setSearchResults([]);
    }
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchTerm, searchContacts]);

  const addContact = (contact: { firstName?: string; lastName?: string; name?: string; email: string }) => {
    const name = contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.email;
    if (!contacts.find(c => c.email.toLowerCase() === contact.email.toLowerCase())) {
      onChange([...contacts, { name, email: contact.email }]);
    }
    setSearchTerm('');
    setShowDropdown(false);
  };

  // Allow typing a raw email and pressing Enter
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchTerm.includes('@')) {
      e.preventDefault();
      addContact({ email: searchTerm.trim(), name: searchTerm.trim() });
    }
  };

  const removeContact = (email: string) => {
    onChange(contacts.filter(c => c.email !== email));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>

      {/* Chips row */}
      {contacts.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {contacts.map(c => (
            <Chip
              key={c.email}
              size="sm"
              variant="flat"
              onClose={() => removeContact(c.email)}
              endContent={<X size={10} />}
            >
              <span className="text-xs">{c.name !== c.email ? `${c.name} <${c.email}>` : c.email}</span>
            </Chip>
          ))}
        </div>
      )}

      {/* Search input */}
      <div ref={containerRef} className="relative">
        <Input
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setShowDropdown(true); }}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          placeholder="Zoek contactpersoon of typ e-mailadres + Enter..."
          startContent={<MagnifyingGlass size={14} className="text-gray-400" />}
          endContent={isSearching && <Spinner size="sm" />}
          size="sm"
        />

        {showDropdown && searchTerm.length >= 2 && (
          <div className="absolute left-0 right-0 top-full mt-1 z-50 max-h-56 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
            {searchResults.length > 0 ? (
              <>
                <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50 border-b">
                  Zoekresultaten
                </div>
                {searchResults.map((contact: any) => (
                  <div
                    key={contact.id}
                    onClick={() => addContact(contact)}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100"
                  >
                    <User size={14} className="text-gray-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm truncate">
                        {contact.firstName} {contact.lastName}
                      </div>
                      <div className="text-xs text-gray-400 truncate">{contact.email}</div>
                    </div>
                  </div>
                ))}
              </>
            ) : !isSearching ? (
              <>
                <div className="px-3 py-3 text-sm text-gray-500 text-center">
                  Geen contacten gevonden voor &ldquo;{searchTerm}&rdquo;
                </div>
                {searchTerm.includes('@') && (
                  <div
                    onClick={() => addContact({ email: searchTerm.trim(), name: searchTerm.trim() })}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 border-t text-sm text-blue-600"
                  >
                    Toevoegen als e-mailadres: {searchTerm}
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
